import { Button, Drawer, message, Space, Splitter } from "antd";
import React, { useContext, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHandleDataHook } from "../../hooks";
import { MiniTableTransactions, MiniTableInvoices, BankReconciliationTable } from "./components";
import { bank_reconciliation_manager } from "../../services";
import { formatDateStampToString, includePercentageAtInvoicesData, includePercentageAtBankTransactionsData, formatToTwoDecimalPlaces } from "./utils";
import { CircleAnimationCompact } from "../loading";
import { useReconciliationManagmentForTransactionsHook } from "./hooks/useReconciliationManagmentForTransactionsHook";
import { ClassificationsContext } from "../../contexts/ClassificationsContext";

export const BankReconciliation = ({
	// Escential Properties
	token,
	bankAccountSelected,
	companySelected,
	// Functions
	onBankAccountSelected,
}) => {
	const { fetchClassificationForBankTransactions } = useContext(ClassificationsContext);

	const [loading, setLoading] = useState(false);	
	
	const [ lastMonthsDetected, setLastMonthsDetected ] = useState({
		firstMonthDetected: "",
		lastMonthDetected: "",
	});

	const [lastInvoicesId, setLastInvoicesId] = useState({
		in: null,
		out: null,
	});
	
	const {
		data: transactionsData,
		dataBackup: transactionsBackupData,
		searchText: searchTransactionText,
		setAllData: setAllTransactionsData,
		clearAllData: clearTransactionsData,
		updateData: updateTransactionData,
		onChangeSearch: onChangeTransactionsSearch,
		onFilterByProperty: onFilterTransactionsByProperty,
		deleteSelectedData: deleteTransactions,
	} = useHandleDataHook();

	const {
		data: invoicesData,
		dataBackup: invoicesBackupData,
		searchText: searchInvoiceText,
		setAllData: setAllInvoicesData,
		clearAllData: clearInvoicesData,
		addMoreData: addMoreInvoicesData,
		updateMultipleData: updateMultipleInvoicesData,
		onChangeSearch: onChangeInvoicesSearch,
		updateBackupData: updateInvoicesBackupData,
		onFilterByProperty: onFilterInvoicesByProperty,
	} = useHandleDataHook();
		
	const {
		selectedTransaction,
		invoicesToSend,
		invoiceRegistersToDelete,
		setSelectedTransaction,
		setInvoicesToSend,
		setInvoiceRegistersToDelete,
		processInvoiceToSend,
		updateInvoiceAmount,
		changeSelectedTransaction,
		fetchSendReconciliation,
		contextHolderFailedInvoices,
	} = useReconciliationManagmentForTransactionsHook(invoicesData, bankAccountSelected.currency);

	const { 
		id: idTransaction,
		amount,
		reconciliation: { invoices: originalInvoices = {}, transaction_consumed: transactionConsumed = false } = {},
	} = selectedTransaction;

	// Filters for Invoices:
	const [filterByPositionInvoice, setFilterByPositionInvoice] = useState("");
	const [filterByStateForInvoice, setFilterByStateForInvoice] = useState("");

	// Filters for Transactions
	const [filterByStateForTransactions, setFilterByStateForTransactions] = useState("");
	
	const onClose = () => {
		onBankAccountSelected(null);
		setLoading(false);
		clearTransactionsData();
		clearInvoicesData();
		setInvoicesToSend({});
		setInvoiceRegistersToDelete([]);
	};
	
	const updateTransactionsData = ({ transactionsToSet, messageRes, datesFounded }) => {
		const transactionsDataFormatted = transactionsToSet.map((data) => {
			return {
				...data,
				date: `${formatDateStampToString(data.date)}`,									
			};
		});

		setAllTransactionsData(includePercentageAtBankTransactionsData(transactionsDataFormatted));
		setLastMonthsDetected({
			firstMonthDetected: datesFounded[0],
			lastMonthDetected: datesFounded[1],
		});
		
		message.info(messageRes);
	};
	
	const updateReconciliationTransactionInFetch = (updatedTransactionInvoices, remainingAmount) => {
		// ! ==> Now its time to update at the UI
		const totalCovered = Object.values(updatedTransactionInvoices).reduce(
			(accumulator, item) => accumulator + item.amount, 0
		) ?? 0;
	
		const total = formatToTwoDecimalPlaces(amount);
		const totalCovering = formatToTwoDecimalPlaces(totalCovered);
	
		const transactionConsumed = remainingAmount === 0.00;
		
		const updatedTransaction = {
			reconciliation: {
				invoices: updatedTransactionInvoices,
				transaction_consumed: transactionConsumed,
			},
			percentage: formatToTwoDecimalPlaces((totalCovering / total) * 100),
		};

		onChangeTransactionsSearch(searchTransactionText);
		updateTransactionData(idTransaction, updatedTransaction);
		setSelectedTransaction((prevTransaction) => ({
			...prevTransaction,
			...updatedTransaction,
		}));

		if(transactionConsumed){
			deleteTransactions([idTransaction]);
			setInvoicesToSend({});			
			
			const index = transactionsBackupData.findIndex(transaction => transaction.id === idTransaction) + 1;
			const indexRecheck = index >= transactionsBackupData.length ? -1 : index;
			const itemTransactionToSet = indexRecheck === -1 ? {} : transactionsBackupData[indexRecheck];

			setSelectedTransaction(itemTransactionToSet);		
			changeSelectedTransaction(itemTransactionToSet, invoicesBackupData, (label, position) => {
				setFilterByPositionInvoice(position);
				onFilterInvoicesByProperty(label, position);
			});
		}
	};
	
	const updateReconciliationInvoicesInFetch = (invoices, invoicesToUpdate, failedInvoicesRegisters) => {
		// ! ==> Now its time to update at the UI
		addMoreInvoicesData(includePercentageAtInvoicesData(invoices)); // ? Set the new invoices
		updateInvoicesBackupData();	// ? Reset the original data
		onChangeInvoicesSearch(searchInvoiceText); // ? Reset the data for the research
		updateMultipleInvoicesData(invoicesToUpdate);
		setInvoicesToSend((prevInvoices) =>
			Object.fromEntries(
				Object.entries(prevInvoices).map(([invoiceId, invoiceData]) => [
					invoiceId,
					failedInvoicesRegisters.includes(invoiceId)
						? invoiceData
						: {
							...invoiceData,
							initial_pendient: invoiceData.pendient,
						},
				])
			)
		);
	};
		
	useEffect(() => {
		const fetchData = async() => {
			setLoading(true);

			try {
				const data = {
					bank_account_id: bankAccountSelected.id,
					company_id: companySelected.id,
				};
				
				const res = await bank_reconciliation_manager(token, "default", data);

				if(!res){
					setLoading(false);
					setLastMonthsDetected({firstMonthDetected: "", lastMonthDetected: ""});
					return;
				}

				const { bank_transactions = {}, invoices = [], lastInvoiceOutId = null, lastInvoiceInId = null } = res;
				
				const { transactions: transactionsToSet = [], message: messageRes = "", dateFounded = "" } = bank_transactions;

				const transactionsDataFormatted = transactionsToSet.map((data) => {
					return {
						...data,
						date: `${formatDateStampToString(data.date)}`
					};
				});
				
				setAllInvoicesData(includePercentageAtInvoicesData(invoices));
				setAllTransactionsData(includePercentageAtBankTransactionsData(transactionsDataFormatted));
				setLastMonthsDetected({ firstMonthDetected: dateFounded, lastMonthDetected: "" });
				setLastInvoicesId({ in: lastInvoiceInId, out: lastInvoiceOutId });
				setLoading(false);
				message.info(messageRes);
				
				// By the moment the classifications will be consulted
				await fetchClassificationForBankTransactions(companySelected.id);

			} catch (err) {
				message.error(err.response.data);
			} finally {
				setLoading(false);
			}			
		};

		if(transactionsData.length > 0 || invoicesData.length > 0) return;

		fetchData();
	}, []);
	
	// UseMeme for optimize the filters for transactions
	const filterTransactionOptions = useMemo(() => ({
		payedState: filterByStateForTransactions,
		onPayedState: (value) => {
			setFilterByStateForTransactions(value);
			onFilterTransactionsByProperty("payedState", value);
		},
	}), [filterByStateForTransactions, onFilterTransactionsByProperty]);
	
	const handleSelectedTransaction = useCallback((value) => {
		changeSelectedTransaction(value, invoicesBackupData, (label, position) => {
			setFilterByPositionInvoice(position);
			onFilterInvoicesByProperty(label, position);
		});
	}, [changeSelectedTransaction, invoicesBackupData, onFilterInvoicesByProperty]);
	
	const handleTransactionChangeSearch = useCallback((text) => {
		onChangeTransactionsSearch(text);
	}, [onChangeTransactionsSearch]);

	// UseMeme for optimize the filters for invoices
	const filterInvoiceOptions = useMemo(() => ({ 
		positionType: filterByPositionInvoice, 
		onPositionType: (value) => {
			setFilterByPositionInvoice(value);
			onFilterInvoicesByProperty("position", value);
		},
		payedState: filterByStateForInvoice,
		onPayedState: (value) => {
			setFilterByStateForInvoice(value);
			onFilterInvoicesByProperty("payedState", value);
		},
	}), [filterByPositionInvoice, onFilterInvoicesByProperty]);
		
	const ReconciliationSlitterView = (
		<Splitter>
			<Splitter.Panel defaultSize="35%" min="30%" max="60%" collapsible>
				{/* TRANSACTIONS VIEW */}
				<MiniTableTransactions
					token={token}
					selectedTransaction={selectedTransaction}
					transactions={transactionsData}						
					lastMonthsDetected={lastMonthsDetected}
					bankAccount={bankAccountSelected}		
					searchText={searchTransactionText}
					filterOptions={filterTransactionOptions}
					onTransactions={updateTransactionsData}
					onSelectedTransaction={handleSelectedTransaction}
					onChangeSearch={handleTransactionChangeSearch}
				/>
			</Splitter.Panel>
			<Splitter.Panel>
				<Splitter layout="vertical">
					<Splitter.Panel style={{ flex: 1, display: "flex", flexDirection: "column" }}>
						{/* RECONCILIATION TABLE VIEW */}
						<BankReconciliationTable
							invoicesToSend={invoicesToSend}
							bankTransaction={selectedTransaction}
							currency={bankAccountSelected.currency}								
							onUpdateInvoiceAmount={updateInvoiceAmount}								
							onRemoveInvoice={({ invoiceId, checked }) => {
								processInvoiceToSend({invoiceId, checked});
								if(!originalInvoices[invoiceId]) return;
								setInvoiceRegistersToDelete([...invoiceRegistersToDelete, invoiceId]);
							}}
						/>
					</Splitter.Panel>
					<Splitter.Panel style={{ flex: 1, display: "flex", flexDirection: "column" }}>
						{/* INVOICES VIEW */}
						<MiniTableInvoices
							token={token}
							invoices={invoicesData}
							invoicesToSend={invoicesToSend}
							companyId={companySelected.id}
							bankAccountId={bankAccountSelected.id}
							enableSelectInvoices={JSON.stringify(selectedTransaction) !== "{}" || transactionConsumed}
							searchText={searchInvoiceText}
							lastInvoicesId={lastInvoicesId}
							filterOptions={filterInvoiceOptions}
							onSendInvoice={({ invoiceId, checked, inInvoicePaid, outInvoicePaid }) => {
								processInvoiceToSend({invoiceId, checked, inInvoicePaid, outInvoicePaid});
								if(!originalInvoices[invoiceId]) return;
								setInvoiceRegistersToDelete([...invoiceRegistersToDelete, invoiceId]);
							}}
							onInvoices={(invoices) => setAllInvoicesData(invoices)}
							onChangeSearch={onChangeInvoicesSearch}
							onSendLastInvoices={setLastInvoicesId}
						/>
					</Splitter.Panel>
				</Splitter>
			</Splitter.Panel>
		</Splitter>
	);
	
	return (
		<Drawer
			title="Reconciliación Bancaria"
			placement="right"
			onClose={onClose}
			open={bankAccountSelected !== null}
			width={"100%"}
			extra={
				<Space>
					{JSON.stringify(selectedTransaction) !== "{}" && <>
						<Button
							loading={loading}
							type="primary"
							disabled={transactionConsumed}
							onClick={() => fetchSendReconciliation({
								token,
								companySelectedId: companySelected.id,
								onLoading: setLoading,
								onUpdateReconciliationInvoicesInFetch: updateReconciliationInvoicesInFetch,
								onUpdateReconciliationTransactionInFetch: updateReconciliationTransactionInFetch,
							})}
						>
							Guardar Cambios
						</Button>
					</>}
				</Space>
			}
		>
			{loading ? <CircleAnimationCompact/> : ReconciliationSlitterView}
			{contextHolderFailedInvoices}
		</Drawer>
	);
};

BankReconciliation.propTypes = {
	token: PropTypes.string.isRequired,
	bankAccountSelected: PropTypes.shape({
		id: PropTypes.string.isRequired,
		currency: PropTypes.string.isRequired,
	}).isRequired,
	companySelected: PropTypes.shape({
		id: PropTypes.string.isRequired,
	}).isRequired,	
	onBankAccountSelected: PropTypes.func,
};